import { z } from "zod";
//#region src/app-metrics.ts
/**
* App-scoped metrics wire types — GET /v1/admin/apps/:appId/metrics
* (site-admin-api observability module; R4 E10).
*
* `:appId` is the `release.apps.id` UUID (a PATH param, resource-scoped by
* `@RequireResourcePermission('cp:app:runtime:read','appId')`); it is NOT part
* of the query schema. `.strict()` (#466) rejects unknown query keys.
*
* ## There is no PromQL on this surface, ever
*
* `chart` is a CLOSED enum and the SERVER owns every expression
* (`APP_METRIC_CHARTS` below). An `expr` / `query` / `promql` key is rejected by
* `.strict()`. A query language cannot be an authorization boundary: the pinned
* catalog is the mechanism, and the pod-name prefix (design/02 §3.2) is the
* scope. Both are composed server-side from validated identifiers, so the
* caller never supplies a selector.
*
* ## Absence has three renderings and they are NOT interchangeable
*
* This is the canon R4 exists to establish, applied to one endpoint:
*  - **`empty`**        — `series: []`, no `unavailable`. The query ran and the
*                         app genuinely has no datapoints in the window.
*  - **`errorstate`**   — a non-2xx. Never a 200 with an empty array.
*  - **`notcollected`** — `unavailable` is present. The query was NOT run
*                         because nothing could answer it. Callers must render
*                         this differently from `empty`; an empty array is
*                         indistinguishable from "your app is idle", which is
*                         the D1 failure mode this endpoint was reshaped to
*                         avoid.
*/
/**
* The charts this endpoint answers for. A closed enum — an unknown `chart` is a
* 400.
*
* ⛔ **`request-rate`, `error-rate` and `p99-latency` ARE IN THIS ENUM AND MUST
* STAY IN IT, even though nothing produces them today.** They answer with
* `unavailable: { reason: 'NO_PRODUCER' }` (see `APP_METRIC_CHARTS` in
* site-admin-api). Do not "tidy" them out.
*
* The reason is this surface's whole thesis — **`empty` ≠ `errorstate` ≠
* `notcollected`** — and `NO_PRODUCER` *is* the archetype of `notcollected`.
* Removing these three collapses that into a fourth, wrong answer: *"that is
* not a thing"*. A developer who asks for their app's request rate and gets a
* 400 concludes the platform has no such concept and goes and builds their own,
* when the true answer is "this is real, nothing emits it yet, here is the
* issue". `series: []` would be worse still — indistinguishable from an idle
* app. (An earlier ruling did omit them; it was retracted as self-contradictory,
* since it simultaneously required a `NO_PRODUCER` contract and deleted its only
* possible users. R4-04c.)
*
* **Measured, not asserted** — per-DP tenant, 7-day window, 2026-08-02:
* `service_name` = `["core-api"]` and `job` = `["core-api","operator"]` on a DP
* hosting NINE tenant apps. Every HTTP series belongs to a platform service; no
* *tenant* app contributes one. App pods receive exactly one OTel env var,
* `OTEL_EXPORTER_OTLP_ENDPOINT` (`platform-injected-env.ts`) — an address with
* nothing pointed at it. Tracked as **#999**.
*
* ⚠️ WHEN A PRODUCER EXISTS, DO NOT COPY THE EXPRESSIONS FROM design/02 §6.1 —
* all four of its claims are wrong against the live tenant, recorded here so
* they are not re-derived from scratch:
*   1. the live metric is `http_server_request_duration_{count,bucket}`
*      (417 series); `http_server_duration_*` is a stale name with **0 series**
*   2. the label is `http_response_status_code`, not `http_status_code`
*   3. stable semconv is **SECONDS** — there is no `/1000`; keeping it reports
*      p99 a thousand times low
*   4. `<group>` cannot be `job` — `job` is the service name, never a pod-name
*      prefix, so it does not scope to an app
*/
const appMetricChartSchema = z.enum([
	"cpu",
	"memory",
	"pod-count",
	"restarts",
	"request-rate",
	"error-rate",
	"p99-latency"
]);
/**
* Display unit, so a client never has to infer one from the chart name.
*
* ⚠️ `reqps` / `percentunit` / `seconds` belong to the three HTTP charts, which
* are `NO_PRODUCER` today. The unit is still declared and still returned —
* "we cannot fill this chart yet" and "we do not know what this chart would
* measure" are different statements, and a client rendering the axis of an
* unavailable chart should not have to guess. `seconds` in particular is
* load-bearing: stable semconv is seconds, and the design doc's `/1000` (which
* assumed milliseconds) is the defect this unit pins against.
*/
const appMetricUnitSchema = z.enum([
	"short",
	"bytes",
	"count",
	"reqps",
	"percentunit",
	"seconds"
]);
/** Query window. Closed enum — the server derives `start`/`end`/`step`. */
const appMetricWindowSchema = z.enum([
	"1h",
	"6h",
	"24h",
	"7d"
]);
/**
* Series grouping.
*
* ⚠️ Only `pod` maps to a real Mimir label. `fixture` is computed SERVER-SIDE:
* the query always groups `by (k8s_pod_name)` and the server parses the
* fixtureId out of each pod name via the deterministic naming spine
* (`<appKey>--<fixtureId>--<versionSlug>-<rsHash>-<podHash>`, R4-14) and
* re-aggregates. There is no fixture label in Mimir and promotion cannot create
* one — the fixture identity lives only in the pod NAME.
*/
const appMetricGroupBySchema = z.enum(["fixture", "pod"]);
/**
* Why a chart cannot be answered.
*
*  - `NO_PRODUCER` — nothing emits this data anywhere. Not actionable by the app
*    owner; it is a platform gap. Used by `request-rate` / `error-rate` /
*    `p99-latency` (#999).
*
* ## ⛔ `NOT_A_TIME_SERIES` WAS RETIRED 2026-08-08 (R-15e / ESC-2), AND ⛔ DO NOT
* ## RE-ADD IT
*
* Its only user was `restarts`, on the reasoning that *"the data EXISTS, but not
* in a shape this endpoint can return"*. That was a **category error**: the
* durable ledger `release.app_restart_events` carries `occurred_at` on every row,
* so a bucketed count over it **is** a time series — it simply is not in Mimir.
* `restarts` is now `kind: 'ledger'` and returns real points, so this member had
* no reachable producer left.
*
* ⛔ The invariant below is why it had to GO rather than be left in place: a
* `reason` whose only chart has stopped pointing at it is a dead branch with
* passing schema tests — which is precisely how `NO_PRODUCER` sat unreachable
* until R4-04c. **Every member must be REACHABLE.** If you find a member with no
* chart pointing at it, delete the member or build the chart; do not leave it.
*
* ⚠️ And if you are tempted to re-add it for a NEW chart: check first that the
* data really has no time dimension. `restarts` looked like it did not, for
* months, while a timestamped ledger was already being written.
*/
const appMetricUnavailableReasonSchema = z.enum(["NO_PRODUCER"]);
/**
* Present iff the chart could not be answered. Its presence — not an empty
* `series` — is what distinguishes `notcollected` from `empty`.
*/
const appMetricUnavailableSchema = z.object({
	reason: appMetricUnavailableReasonSchema,
	/** What is missing, in one sentence a developer can act on or escalate. */
	detail: z.string().min(1),
	/** The concrete next step. Never empty — "unavailable, good luck" is not a
	*  contract. For `NO_PRODUCER` it names what would have to be built or
	*  enabled, and deliberately promises no date. */
	remedy: z.string().min(1),
	/** Where the gap is tracked, so the answer has an expiry date. */
	trackingIssue: z.string().min(1)
}).strict();
/** Query params. `.strict()` — an unknown key (notably `expr`) is a 400. */
const appMetricsQuerySchema = z.object({
	chart: appMetricChartSchema,
	/**
	* ⛔ REQUIRED. Omitting it is a 400 naming the reason.
	*
	* design/02 specified `dpId?` with "omitted ⇒ one line per DP", but a Mimir
	* tenant is selected by the TRANSPORT (`X-Scope-OrgID`), never by a label —
	* so a fleet view is N sequential cross-tenant queries on the first call
	* every surface makes. That is the fan-out design rule R3 forbids, and it
	* is the same call R4-07 made for E7's `dpId`.
	*/
	dpId: z.string().uuid(),
	/** Narrow to one fixture's pods. DNS-1123-bounded; escaped server-side. */
	fixtureId: z.string().min(1).max(63).optional(),
	window: appMetricWindowSchema,
	/** Defaults to `fixture` server-side when omitted. */
	groupBy: appMetricGroupBySchema.optional()
}).strict();
/** One line on the chart. `null` marks a gap rather than imputing a value. */
const appMetricSeriesSchema = z.object({
	/** Grouping keys, e.g. `{ fixtureId: 'api' }` or `{ pod: '<name>' }`. */
	labels: z.record(z.string(), z.string()),
	/** `[unixSeconds, value|null]`. `null` = no sample; never coerced to 0,
	*  which would draw a plausible dip where there is simply no data. */
	points: z.array(z.tuple([z.number(), z.number().nullable()]))
}).strict();
/**
* A per-DP caveat about COVERAGE — the window was queried but the answer is
* known to be partial. Distinct from `unavailable` (nothing was queried) and
* from an empty `series` (queried, genuinely nothing there).
*/
const appMetricIncompleteSchema = z.object({
	dpId: z.string().uuid(),
	reason: z.string().min(1)
}).strict();
/** One chart's data. */
const appMetricsResponseSchema = z.object({
	/** Echoes the requested app UUID (`release.apps.id`) verbatim. */
	appId: z.string().uuid(),
	chart: appMetricChartSchema,
	unit: appMetricUnitSchema,
	/** Empty ⇔ `empty` when `unavailable` is absent; always `[]` when it is
	*  present, so a client that ignores `unavailable` degrades to "no data"
	*  rather than rendering a wrong number. */
	series: z.array(appMetricSeriesSchema),
	window: z.object({
		start: z.string().datetime(),
		end: z.string().datetime(),
		/** Prometheus step, e.g. `60s`. */
		step: z.string().min(1)
	}).strict(),
	/**
	* Known-partial coverage, per DP. Populated for at least one case the spec
	* never anticipated: **k8s label promotion is FORWARD-ONLY.** Series written
	* before the promotion transform was deployed carry no `k8s_pod_name`, so a
	* `7d` window in the first week after rollout is legitimately part-empty.
	* Reporting that beats a chart that silently starts mid-graph.
	*/
	incomplete: z.array(appMetricIncompleteSchema),
	/** Present ⇒ `notcollected`. Absent ⇒ the query actually ran. */
	unavailable: appMetricUnavailableSchema.optional()
}).strict();
//#endregion
export { appMetricChartSchema, appMetricGroupBySchema, appMetricIncompleteSchema, appMetricSeriesSchema, appMetricUnavailableReasonSchema, appMetricUnavailableSchema, appMetricUnitSchema, appMetricWindowSchema, appMetricsQuerySchema, appMetricsResponseSchema };

//# sourceMappingURL=app-metrics.js.map